import React from "react";
import {
  NullCheckedSanityImage,
  SanityImageSourceExt
} from "../models/shared/SanityImageSourceExt";
import { getImageAltText, validImage } from "../utilities/image";
import imageBuilder from "./sanity/imageBuilder";

export type SanityImageProps = {
  className?: string;
  image?: SanityImageSourceExt;
  loading?: "eager" | "lazy";
  height?: number;
  width?: number;
};

/**
 * Helps setting the "width" and "height" attribute on the img tag, even if either width or height
 * isn't explicitly set (i.e. we use source values to fill out the gaps)
 * @param source Source image asset
 * @param width Specified width during processing
 * @param height Specified height during processing
 */
const getImageDimensions = (
  source: NullCheckedSanityImage,
  width?: number,
  height?: number
) => {
  const dimensions = {
    width,
    height
  };

  const aspectRatio = source.asset.metadata?.dimensions?.aspectRatio;

  if (!aspectRatio) {
    // Can't do proper calculations without this
    return dimensions;
  }

  if (width && !height) {
    dimensions.height = Math.round(width / aspectRatio);
  }

  if (!width && height) {
    dimensions.width = Math.round(height * aspectRatio);
  }

  if (!width && !height) {
    dimensions.width = source.asset.metadata?.dimensions?.width;
    dimensions.height = source.asset.metadata?.dimensions?.height;
  }

  return dimensions;
};

const SanityImage = ({
  className = "",
  image,
  loading = "lazy",
  width,
  height
}: SanityImageProps) => {
  if (!validImage(image)) {
    return null;
  }

  let processedImage = imageBuilder(image).fit("scale").auto("format");

  if (width) {
    processedImage = processedImage.width(width);
  }

  if (height) {
    processedImage = processedImage.height(height);
  }

  return (
    <img
      className={className}
      alt={getImageAltText(image)}
      loading={loading}
      src={processedImage.url({ friendlyFilename: true })}
      {...getImageDimensions(image, width, height)}
    />
  );
};

export default SanityImage;
